import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BreakableRenderer, BusinessComponent, BusinessComponentOptions, ColumnComposition, ColumnCompositions, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { CellData, ComponentData, DateFormatter, TableData, TabUtil } from '@ddt-web/data-service';
import { DateUtil, DcrUpdateUtil, SlugifyPipe } from '@ddt-web/generic-utils';
import { DisclosureComponent, HeadingComponent, HeadingLevel, HeadingWithDateComponent } from '@ddt-web/pdf-components';
import { DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES } from '@ddt-web/pdf-utils';
import { ApplicationState } from '@ddt-web/shared-stores';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as zip from 'lodash/zip';

export class FamilyPerformanceComponent extends BusinessComponent {
	private performanceTableData: TableData;
	private performanceBenchmarkTableData: TableData;
	private performanceCustomNetTableData: TableData;
	private customAdvisoryFee: number;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PERFORMANCE_BENCHMARK) private readonly enablePerformanceBenchmarkData: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_NET_PERFORMANCE) private readonly enableNetPerformanceData: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PERFORMANCE_BOX_DISCLOSURE) private readonly enablePerformanceBoxDisclosure: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.PERFORMANCE_TABLE_DISABLE_COLUMNS_LIST_PDF) private readonly excludeColumns: string[] = [];
	@InjectConfig(ModelsExperienceConfigKey.DATE_FORMAT) private readonly dateFormat: string = DateUtil.MODELS_US_DATE_FORMAT;
	@InjectConfig(ModelsExperienceConfigKey.LEFT_ALIGNED_COLUMNS_INDEX) private readonly leftAlignedColumnsIndex: number = 2;
	@InjectConfig(ModelsExperienceConfigKey.USE_H1_TITLE_PDF) private readonly useH1Title: boolean = false;

	constructor(
		name: string,
		public dataComponentId: string,
		public visible: boolean = true,
		public aladdinTicker: string = '',
		public inceptionDateText: string = '',
		public includePerformance: boolean = true,
		public familySubtypeDcrPrefix: string = '',
		public isFamilyPerformanceDisclaimerBoxVisible: boolean = true,
		public modelBenchmarkDisclaimer: string | undefined = undefined
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		const isComponentDataSuccessful = componentData && componentData.success;

		const performanceTabName = 'performance';
		const performanceTab = isComponentDataSuccessful && componentData.getTabById(performanceTabName);
		this.performanceTableData = TabUtil.isTabValid(performanceTab) && get(performanceTab, 'tableData[0]', []);

		const performanceBenchmarkTabName = 'performance.benchmark';
		const performanceBenchmarkTab = isComponentDataSuccessful && componentData.getTabById(performanceBenchmarkTabName);
		this.performanceBenchmarkTableData = TabUtil.isTabValid(performanceBenchmarkTab) && get(performanceBenchmarkTab, 'tableData[0]', []);
		const performanceCustomNetTab = isComponentDataSuccessful && componentData.getTabById('performance.custom.net');
		this.performanceCustomNetTableData = TabUtil.isTabValid(performanceCustomNetTab) && get(performanceCustomNetTab, 'tableData[0]', []);
		return this.includePerformance && !isEmpty(this.performanceTableData) && !isEmpty(this.performanceBenchmarkTableData);
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService, applicationState: ApplicationState): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];
		const familyIdsDcrKey = new SlugifyPipe().transform(this.aladdinTicker, '_')?.toUpperCase();
		this.customAdvisoryFee = get(applicationState, 'performanceState.customAdvisoryFee');
		if (this.isComponentDataVisible(componentData)) {
			const asOfDate = get(this.performanceTableData, 'rows[0].asOfDate.formattedValue');
			const asOfDateNet = get(this.performanceCustomNetTableData, 'rows[0].asOfDate.formattedValue');
			const headingLevel = this.useH1Title ? HeadingLevel.H1 : HeadingLevel.H2;

			if (!asOfDate || !asOfDateNet || asOfDate === asOfDateNet || !this.customAdvisoryFee) {
				const combinedCellComponentData = this.getCombinedCellsTableData();
				panels.push(
					new HeadingWithDateComponent().buildHeader(
						'Heading',
						'PDF_FAMILY_PERFORMANCE_H2',
						'Performance (%)',
						new DateFormatter(this.dateFormat).format(asOfDate) || '',
						null,
						false,
						headingLevel
					)
				);
				panels.push(
					...this.getFamilyPerformancePanels(combinedCellComponentData, contentSvc, applicationState),
					new StaticParagraphComponent(`PDF_${familyIdsDcrKey}_PERF_INCEPTION_DATE`, `${this.inceptionDateText}`, {}, { paddingTop: 5 })
				);
				if (this.modelBenchmarkDisclaimer) {
					panels.push(
						new DisclosureComponent(
							`${this.modelBenchmarkDisclaimer}`,
							`${this.modelBenchmarkDisclaimer}`,
							{},
							{ paddingTop: 10 },
							DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES
						)
					);
				} else {
					panels.push(
						new DisclosureComponent(
							`PDF_${familyIdsDcrKey}_DISCLOSURE`,
							contentSvc.get(`${familyIdsDcrKey}_DISCLOSURE`, `${familyIdsDcrKey}_DISCLOSURE`),
							{},
							{ paddingTop: 10 },
							DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES
						)
					);
				}

				if (!this.customAdvisoryFee) {
					panels.push(new DisclosureComponent(`NO_NET_PERF_DISCLOSURE`, 'NO_NET_PERF_DISCLOSURE', {}, { paddingTop: 5 }, DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES));
				}
				panels.push(
					new DisclosureComponent(
						`PDF_${this.familySubtypeDcrPrefix}FAMILY_PERFORMANCE_DISCLAIMER_PREFIX`,
						`PDF_${this.familySubtypeDcrPrefix}FAMILY_PERFORMANCE_DISCLAIMER_PREFIX`,
						{},
						{ paddingTop: 5, paddingBottom: 4 },
						DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES
					));
				if (this.isFamilyPerformanceDisclaimerBoxVisible && this.enablePerformanceBoxDisclosure) {
					panels.push(
						new DisclosureComponent(
							`PDF_${this.familySubtypeDcrPrefix}FAMILY_PERFORMANCE_DISCLAIMER_BOX`,
							`PDF_${this.familySubtypeDcrPrefix}FAMILY_PERFORMANCE_DISCLAIMER_BOX`,
							{},
							{
								borderTop: 0.5,
								borderLeft: 0.5,
								borderRight: 0.5,
								borderBottom: 0.5,
								paddingTop: 1,
								paddingLeft: 1,
								paddingRight: 1,
								paddingBottom: 1
							},
							DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES
						)
					);
				}

				panels.push(new DisclosureComponent(
					`PDF_${this.familySubtypeDcrPrefix}FAMILY_PERFORMANCE_DISCLAIMER_SUFFIX`,
					`PDF_${this.familySubtypeDcrPrefix}FAMILY_PERFORMANCE_DISCLAIMER_SUFFIX`,
					{},
					{ paddingTop: 4, paddingBottom: 5 },
					DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES));
			} else {
				panels.push(
					new HeadingComponent('PDF_FAMILY_PERFORMANCE_H2', 'Performance (%)', headingLevel),
					new StaticParagraphComponent('PERFORMANCE_HOLDINGS_ERROR_PDF', 'The performance data is currently not available for this model', {
						paddingTop: 10,
						paddingBottom: 10
					})
				);
			}
		}
		return panels;
	}

	private getFamilyPerformancePanels(tableData: TableData, contentSvc?: ContentService, applicationState?: ApplicationState): Array<AbstractRenderer<SVGGElement>> {
		const rows: Array<RowPanel> = [];
		const columnConfigurations = this.getColumnComposition(tableData, contentSvc, applicationState);

		if (!columnConfigurations || !columnConfigurations.headers || !columnConfigurations.body) {
			console.error(`Column configurations are not defined, table: ${this.name} will not render!`);
			return [];
		}

		const headerRow: RowPanel = new RowPanel(
			'FamilyPerformanceComponent Header Row',
			columnConfigurations.headers.map((config: ColumnComposition) => config.renderer.render()),
			{ paddingBottom: 2, borderBottom: 0.25, borderBottomColor: '#000', marginBottom: 2 },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);
		rows.push(headerRow);

		columnConfigurations.body.forEach((configs: Array<ColumnComposition>, index) => {
			const indexToAddBackGround = 2 + (this.performanceCustomNetTableData && this.customAdvisoryFee ? 1 : 0);
			const shouldAddBackgroundColor = (index + 1) % indexToAddBackGround === 0;

			const rowRenderer = configs.map((config: ColumnComposition) => config.renderer.render());
			rows.push(
				new RowPanel(
					'FamilyPerformanceComponent Body Row',
					rowRenderer,
					{ paddingTop: 2, paddingBottom: 2, backgroundColor: shouldAddBackgroundColor ? '#efefef' : '' },
					Alignment.Horizontal.Left,
					Alignment.Vertical.Middle
				)
			);
		});

		return [new BreakableRenderer(rows, { paddingLeft: 5, paddingRight: 5, paddingTop: 20 }, headerRow)];
	}

	private getColumnComposition(tableData: TableData, contentSvc: ContentService, applicationState?: ApplicationState): ColumnCompositions {
		const tableHeaders: Array<CellData> = get(tableData, 'headersX', []).filter((header: CellData) => header.config && get(header, 'config.visible')
			&& !this.excludeColumns.includes(header?.config?.id)
		);
		const columnProportions = this.getColumnProportions(tableHeaders);

		const columnHeaderConfigs = tableHeaders.map((header: CellData, index: number) => {
			return new ColumnComposition({
				cells: header,
				widthProportion: columnProportions[index],
				textClasses: ['text-bold'],
				horizontalAlignment: index < this.leftAlignedColumnsIndex ? Alignment.Horizontal.Left : Alignment.Horizontal.Right,
				verticalAlignment: Alignment.Vertical.Bottom,
				boxModelConstraint: { paddingLeft: 9, paddingRight: 9 }
			});
		});

		const columnBodyConfigs = get(tableData, 'cells', [])
			.filter((cellDataArray: Array<CellData>) => cellDataArray[0].config && get(cellDataArray[0], 'config.visible'))
			.map((cellDataArray: Array<CellData>, index: number) => {
				return cellDataArray
					.filter((cell: CellData) => cell.config && get(cell, 'config.visible')
						&& !this.excludeColumns.includes(cell?.config?.id)
					)
					.map((cell: CellData, index: number) => {
						if (cell.config.id === 'netPerformanceType') {
							cell.formattedValue += ' ' + DcrUpdateUtil.getAdvisoryFeeText(contentSvc);
						}
						if (cell.config.id === 'netCustomPerformanceType') {
							cell.formattedValue += ' ' + DcrUpdateUtil.getAdvisoryFeeText(contentSvc, get(applicationState, 'performanceState.customAdvisoryFee'));
						}
						return new ColumnComposition({
							cells: cell,
							widthProportion: columnProportions[index],
							textClasses: ['text-light'],
							horizontalAlignment: index < this.leftAlignedColumnsIndex ? Alignment.Horizontal.Left : Alignment.Horizontal.Right,
							verticalAlignment: Alignment.Vertical.Middle,
							boxModelConstraint: { paddingLeft: 9, paddingRight: 9 }
						});
					});
			});

		return new ColumnCompositions(columnHeaderConfigs, columnBodyConfigs);
	}

	private getCombinedCellsTableData(): TableData {
		const performanceCells = get(this.performanceTableData, 'cells', []).map(cellData => cellData.filter(cell => cell.config.visible));
		const performanceBenchmarkCells = this.enablePerformanceBenchmarkData ?
			get(this.performanceBenchmarkTableData, 'cells', []).map(cellData => cellData.filter(cell => cell.config.visible)) : [];
		let performanceCustomNetCells;
		if (this.customAdvisoryFee) {
			performanceCustomNetCells = this.enableNetPerformanceData ?
				get(this.performanceCustomNetTableData, 'cells', []).map(cellData => cellData.filter(cell => cell.config.visible)) : [];
		}
		const availableCells = [performanceCells, performanceCustomNetCells, performanceBenchmarkCells].filter(item => item?.length);

		const combinedCells = flatten(zip(...availableCells));
		const combinedCellsTableData: TableData = Object.assign(this.performanceTableData, { cells: combinedCells });

		return combinedCellsTableData;
	}

	private getColumnProportions(tableHeaders: Array<CellData>): Array<number> {
		switch (tableHeaders.length) {
			case 5:
				return [24, 19, 19, 19, 19];
			case 6:
				return [20, 16, 16, 16, 16, 16];
			case 7:
				return [16, 14, 14, 14, 14, 14, 14];
			case 8:
				return [16, 12, 12, 12, 12, 12, 12, 12];
			case 9:
				return [19, 18, 9, 9, 9, 9, 9, 9, 9];
			case 10:
				return [18, 18, 8, 8, 8, 8, 8, 8, 8, 8];
			default:
				console.error('Number of columns was not expected - received: ' + tableHeaders.length);
				return [];
		}
	}
}
